// Eskom PARCC — CES working-paper workbook parser (R5 #1).
//
// PURE (no React, no Firebase): turns the consultants' completed working-paper
// workbook into per-sheet, per-transaction capture material. The workbook shape
// (verified against Nkosi's 13 Jul 2026 NTCSA paper):
//
//   • one sheet per procurement route (`Formal Tendering - Contracts`, `LPO`,
//     `Urgent Post LS `, … — some names carry trailing spaces);
//   • row 1: transaction header labels (Division … End-User; 10 or 11 columns
//     depending on the route) followed by MERGED criteria-group titles;
//   • row 2: alternating `Sub-Criteria` / `Compliance Level` pair labels —
//     the structural anchor (merged row-1 titles read blank off-anchor);
//   • row 3: the sub-criterion text per pair;
//   • rows 4+: one transaction per row — header cells, then an evidence/level
//     cell PAIR per sub-criterion, a trailing `Additional Comments/Findings`
//     column, and (LPO) a dated `GAPS Status <date>` column.
//
// IMPORTANT: the sheet's OWN sub-criterion text is preserved as the captured
// criterion. Consultant workbooks routinely lag PARCC's refreshed rubric (the
// 13 Jul paper uses the pre-workshopv2 NTCSA wording, where the same "1.2"
// code names a different procedure) — so codes/labels are NEVER remapped onto
// the current template; the paper is the record of what was actually assessed.
//
// The matrix-level core (`parseCesSheets`) is what unit tests drive;
// `parseCesWorkbook` is the thin SheetJS byte-level wrapper, mirroring the
// bulk-import parser's parseWorkbook/matrixToRecords split.
import * as XLSX from 'xlsx';
import { CES_STATUS_OPTIONS } from '../../domain/enums';
import { normHeaderLabel } from './cesHeaderFields';

/** One sub-criterion column pair discovered on a sheet. */
export interface CesSheetCriterion {
  /** 0-based pair position (becomes the captured row's seq - 1). */
  pairIndex: number;
  /** 0-based column of the pair's Sub-Criteria (evidence) cell. */
  startCol: number;
  /** The sheet's own sub-criterion text (row 3) — stored verbatim as the criterion. */
  text: string;
  /** The criteria-group title the pair sits under (row 1, carried forward). */
  stage: string;
}

/** One evidence/level pair with content, on one transaction row. */
export interface CesUploadEntry {
  pairIndex: number;
  evidence: string;
  /** The normalised CES_STATUS_OPTIONS value ('' when blank or unrecognised). */
  status: string;
  /** The raw level cell as typed. */
  statusRaw: string;
  /** False when a non-blank level cell did not fold onto the CES status scale. */
  recognised: boolean;
}

/** One transaction row parsed off a route sheet. */
export interface CesUploadRow {
  sheetName: string;
  /** 1-based Excel row for operator-friendly messages. */
  excelRow: number;
  /** Normalised header label → cell text (see normHeaderLabel). */
  header: Record<string, string>;
  /** The Contract/PO Number cell — the match key against the register. */
  referenceNo: string;
  entries: CesUploadEntry[];
  additionalComments: string;
  gapsStatusRaw: string;
  warnings: string[];
}

/** One parsed route sheet. */
export interface CesUploadSheet {
  sheetName: string;
  /** The canonical template/mechanism key, when the sheet name is a known route. */
  mechanismKey?: string;
  criteria: CesSheetCriterion[];
  rows: CesUploadRow[];
  warnings: string[];
}

export interface CesUploadParse {
  sheets: CesUploadSheet[];
  /** Non-route sheets (Legends, Sheet1, …) — informational only. */
  ignoredSheets: string[];
}

/** Sheet-name → canonical template key (trim/case/qualifier tolerant). */
export function resolveSheetMechanism(sheetName: string): string | undefined {
  const n = sheetName.toLowerCase().replace(/\s+/g, ' ').trim();
  if (n === 'formal tendering - contracts') return 'Formal Tendering - Contracts';
  if (n === "informal tendering - po's" || n === 'informal tendering - pos') return "Informal Tendering - PO's";
  if (n === 'sole source') return 'Sole Source';
  if (n === 'single source') return 'Single Source';
  if (n === 'lpo') return 'LPO';
  if (n.startsWith('urgent')) return 'Urgent';
  if (n.startsWith('emergency')) return 'Emergency';
  if (n === 'expansions - modifications') return 'Expansions - Modifications';
  if (n === 'task order' || n === 'task orders') return 'Task Order';
  if (n.startsWith('transversal')) return 'Transversal';
  if (n.startsWith('multiple source')) return 'Multiple Sources';
  return undefined;
}

/**
 * Fold a workbook compliance-level cell onto the closed CES status scale
 * (`CES_STATUS_OPTIONS`). Tolerant of case, en-dashes and whitespace drift.
 * `Possible IE` folds to the PFMA status (the register's IE carrier).
 *
 * NEGATION IS TESTED FIRST: a cell is only tiered as non-compliant when it
 * actually says "non-compliant/non-compliance" — otherwise a cell like
 * "Compliant per PFMA requirements" would substring-match a tier and invert a
 * compliant assessment into a finding. An untiered "Non-Compliant" (no PFMA /
 * Internal / Housekeeping qualifier) is UNRECOGNISED, not guessed.
 * Unrecognised non-blank values return `{ status: '', recognised: false }` so
 * the evidence still lands while the row carries a warning — never a silent
 * drop, never an invented status.
 */
export function normalizeCesStatus(raw: string): { status: string; recognised: boolean } {
  const cleaned = String(raw ?? '')
    .replace(/[–—]/g, '-') // en/em dashes → hyphen
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned === '') return { status: '', recognised: true };
  const n = cleaned.toLowerCase();
  const exact = CES_STATUS_OPTIONS.find((o) => o.toLowerCase() === n);
  if (exact !== undefined) return { status: exact, recognised: true };
  if (/\bnon[- ]?complian(t|ce)\b/.test(n)) {
    if (n.includes('housekeeping')) return { status: 'Non-Compliant - Housekeeping Issue', recognised: true };
    if (n.includes('pfma') || n.includes('possible ie')) return { status: 'Non-Compliant - PFMA', recognised: true };
    if (n.includes('internal')) {
      return { status: 'Non-Compliant - Internal Policies, Procedures, Instructions etc.', recognised: true };
    }
    return { status: '', recognised: false }; // untiered non-compliance — never guess a tier
  }
  if (/^not[- ]applicable\b|^n\/?a$/.test(n)) return { status: 'Not-Applicable', recognised: true };
  if (n.includes('possible ie')) return { status: 'Non-Compliant - PFMA', recognised: true };
  if (/^compliant\b/.test(n)) return { status: 'Compliant', recognised: true };
  return { status: '', recognised: false };
}

const cell = (matrix: string[][], r: number, c: number): string =>
  String(matrix[r]?.[c] ?? '').trim();

/** Parse ONE route sheet's matrix (rows 1–3 headers, 4+ transactions). */
function parseSheetMatrix(sheetName: string, matrix: string[][]): CesUploadSheet {
  const sheet: CesUploadSheet = {
    sheetName,
    mechanismKey: resolveSheetMechanism(sheetName),
    criteria: [],
    rows: [],
    warnings: [],
  };

  // Locate the pair-label row — the structural anchor. The consultants' papers
  // put it at row 2, but scan the first few rows defensively.
  let pairRow = -1;
  for (let r = 0; r < Math.min(matrix.length, 6); r++) {
    if ((matrix[r] ?? []).some((v) => String(v ?? '').trim() === 'Sub-Criteria')) {
      pairRow = r;
      break;
    }
  }
  if (pairRow < 0) {
    sheet.warnings.push(
      `Sheet "${sheetName}": no Sub-Criteria/Compliance Level header pairs found — the sheet was skipped.`,
    );
    return sheet;
  }
  const labelRow = Math.max(0, pairRow - 1); // row 1 — header labels + group titles
  const criteriaRow = pairRow + 1; // row 3 — sub-criterion text
  const firstDataRow = criteriaRow + 1; // row 4 — transactions

  const labels = matrix[labelRow] ?? [];
  const pairLabels = matrix[pairRow] ?? [];
  const width = Math.max(labels.length, pairLabels.length, matrix[criteriaRow]?.length ?? 0);

  // Discover the pairs; carry the merged group title forward across pairs.
  let firstPairCol = -1;
  let stage = '';
  for (let c = 0; c < width; c++) {
    if (String(pairLabels[c] ?? '').trim() !== 'Sub-Criteria') continue;
    if (firstPairCol < 0) firstPairCol = c;
    const title = cell(matrix, labelRow, c);
    if (title) stage = title;
    sheet.criteria.push({
      pairIndex: sheet.criteria.length,
      startCol: c,
      text: cell(matrix, criteriaRow, c),
      stage: stage || sheetName.trim(),
    });
  }

  // Header labels = everything left of the first pair.
  const headerCols: Array<{ col: number; key: string }> = [];
  for (let c = 0; c < firstPairCol; c++) {
    const label = String(labels[c] ?? '').trim();
    if (label) headerCols.push({ col: c, key: normHeaderLabel(label) });
  }
  const refCol = headerCols.find((h) => h.key === 'contract po number')?.col ?? -1;
  if (refCol < 0) {
    sheet.warnings.push(`Sheet "${sheetName}": no "Contract/PO Number" column — its rows cannot be matched.`);
  }

  // Trailing columns (right of the last pair's level cell): Additional
  // Comments (row-1 label) and the dated GAPS Status (row-1 OR row-3 label).
  const lastPair = sheet.criteria[sheet.criteria.length - 1];
  const trailingStart = lastPair ? lastPair.startCol + 2 : firstPairCol;
  let commentsCol = -1;
  let gapsCol = -1;
  for (let c = trailingStart; c < width + 2; c++) {
    const label = cell(matrix, labelRow, c) || cell(matrix, criteriaRow, c);
    if (/^additional comments/i.test(label)) commentsCol = c;
    else if (/^gaps\s*status/i.test(label)) gapsCol = c;
  }

  // Transaction rows: any row whose header block has content.
  for (let r = firstDataRow; r < matrix.length; r++) {
    const hasHeader = headerCols.some(({ col }) => cell(matrix, r, col) !== '');
    const referenceNo = refCol >= 0 ? cell(matrix, r, refCol) : '';
    if (!hasHeader && referenceNo === '') continue;

    const row: CesUploadRow = {
      sheetName,
      excelRow: r + 1,
      header: {},
      referenceNo,
      entries: [],
      additionalComments: commentsCol >= 0 ? cell(matrix, r, commentsCol) : '',
      gapsStatusRaw: gapsCol >= 0 ? cell(matrix, r, gapsCol) : '',
      warnings: [],
    };
    for (const { col, key } of headerCols) row.header[key] = cell(matrix, r, col);

    for (const c of sheet.criteria) {
      const evidence = cell(matrix, r, c.startCol);
      const statusRaw = cell(matrix, r, c.startCol + 1);
      if (evidence === '' && statusRaw === '') continue;
      const { status, recognised } = normalizeCesStatus(statusRaw);
      if (!recognised) {
        row.warnings.push(
          `Row ${row.excelRow}: compliance level "${statusRaw}" (under "${c.text.slice(0, 60)}…") is not on the CES scale — captured without a status.`,
        );
      }
      row.entries.push({ pairIndex: c.pairIndex, evidence, status, statusRaw, recognised });
    }
    sheet.rows.push(row);
  }
  return sheet;
}

/** The pure matrix-level core: every sheet's matrix → the parsed upload. */
export function parseCesSheets(sheets: Record<string, string[][]>): CesUploadParse {
  const out: CesUploadParse = { sheets: [], ignoredSheets: [] };
  for (const [name, matrix] of Object.entries(sheets)) {
    if (resolveSheetMechanism(name) === undefined) {
      out.ignoredSheets.push(name);
      continue;
    }
    out.sheets.push(parseSheetMatrix(name, matrix));
  }
  return out;
}

/** SheetJS byte-level wrapper: workbook file → per-sheet matrices → parse. */
export function parseCesWorkbook(input: ArrayBuffer): CesUploadParse {
  const wb = XLSX.read(input, { type: 'array' });
  const matrices: Record<string, string[][]> = {};
  for (const name of wb.SheetNames) {
    matrices[name] = XLSX.utils.sheet_to_json<string[]>(wb.Sheets[name], {
      header: 1,
      defval: '',
      raw: false,
      blankrows: true,
    }) as string[][];
  }
  return parseCesSheets(matrices);
}
